import {
  createMockDriver,
  loadMockDriver,
  saveMockDriver,
} from "../../features/driver/mockIdentity";
import { mockLocationFor } from "../../features/location/coarseLocation";
import {
  targetSpeedMph,
  trafficStateForSpeed,
} from "../../features/traffic/trafficModel";
import type { DriverProfile, TrafficSnapshot } from "../../types/traffic";
import type {
  HeartbeatInput,
  HeartbeatResponse,
  JoinInput,
  JoinResponse,
  TrafficApi,
} from "./TrafficApi";

export class MockTrafficApi implements TrafficApi {
  private driverCount = 24;
  private profile: DriverProfile = loadMockDriver() ?? createMockDriver();
  private sessionDistanceMiles = 0;

  public setDriverCount(driverCount: number): void {
    this.driverCount = Math.min(100_000, Math.max(1, Math.floor(driverCount)));
  }

  public join(_input: JoinInput = {}): Promise<JoinResponse> {
    this.profile = loadMockDriver() ?? this.profile;
    this.sessionDistanceMiles = 0;
    saveMockDriver(this.profile);
    const now = new Date().toISOString();
    return Promise.resolve({
      sessionId: `session_${crypto.randomUUID().replaceAll("-", "")}`,
      driver: this.profile,
      location: mockLocationFor(this.profile.plate),
      traffic: this.snapshot(),
      cohort: { id: "mock-cohort", channel: "mock-traffic" },
      serverTime: now,
      heartbeatIntervalSeconds: 30,
      configurationRevision: 1,
      features: {
        realtimeMode: "mock",
        garageEnabled: true,
        customPlatesEnabled: false,
        sharingEnabled: true,
      },
    });
  }

  public heartbeat(input: HeartbeatInput): Promise<HeartbeatResponse> {
    const acceptedDistance = Math.min(2, Math.max(0, input.odometerDeltaMiles));
    this.sessionDistanceMiles += acceptedDistance;
    if (input.visible && input.odometerDeltaMiles > 0) {
      this.profile = {
        ...this.profile,
        odometerMiles: this.profile.odometerMiles + acceptedDistance,
        ...input.appearance,
      };
      saveMockDriver(this.profile);
    }
    return Promise.resolve({
      traffic: this.snapshot(),
      configurationRevision: 1,
      realtimeMode: "full",
      heartbeatIntervalSeconds: 30,
      sessionDistanceMiles: this.sessionDistanceMiles,
    });
  }

  public leave(): Promise<void> {
    this.profile = loadMockDriver() ?? this.profile;
    saveMockDriver(this.profile);
    return Promise.resolve();
  }

  private snapshot(): TrafficSnapshot {
    const speed = targetSpeedMph(this.driverCount);
    return {
      activeDriverCount: this.driverCount,
      targetSpeedMph: speed,
      state: trafficStateForSpeed(speed),
      snapshotAt: new Date().toISOString(),
    };
  }
}
